import { useEffect, useRef } from "react";
import { appConfig } from "../apiConfig";

const CITIES = {
  Casablanca:    { lat: 33.5731, lng: -7.5898 },
};

const ORIGIN = CITIES.Casablanca;

function decodePolyline(encoded) {
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    coords.push([lat / 1e5, lng / 1e5]);
  }
  return coords;
}

async function fetchRoute(origin, coordinates) {
  const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${coordinates[0]},${coordinates[1]}?overview=full&geometries=polyline`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.code !== "Ok") return null;
  const route = data.routes[0];
  return {
    coords: decodePolyline(route.geometry),
    distance: (route.distance / 1000).toFixed(0) + " km",
    duration: Math.round(route.duration / 3600) + "h " + Math.round((route.duration % 3600) / 60) + "min",
  };
}

export default function MapDirection({ city , coordinates }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const routeLayerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const infoRef = useRef(null);
  const cityRef = useRef(city); // 👈 track latest city for use inside init callback

  // Keep cityRef in sync
  useEffect(() => {
    cityRef.current = city;
  }, [city]);

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
  }, []);

  const drawRoute = async (city) => {
    const map = mapInstance.current;
    const L = window.L;
    if (!map || !L || !city) return;


    if (routeLayerRef.current) map.removeLayer(routeLayerRef.current);
    if (destMarkerRef.current) map.removeLayer(destMarkerRef.current);

    if (infoRef.current) {
      infoRef.current.innerHTML = "Loading route...";
      infoRef.current.style.display = "block";
    }

    const route = await fetchRoute(ORIGIN, coordinates);
    if (!route) return;

    const polyline = L.polyline(route.coords, {
      color: "#2563eb", weight: 5, opacity: 0.85,
      lineJoin: "round", lineCap: "round",
    }).addTo(map);

    const outline = L.polyline(route.coords, {
      color: "#93c5fd", weight: 8, opacity: 0.25,
      lineJoin: "round", lineCap: "round",
    }).addTo(map);

    const destIcon = L.divIcon({
      className: "",
      html: `<div style="width:20px;height:20px;background:#dc2626;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 12px rgba(220,38,38,0.5);display:flex;align-items:center;justify-content:center">
         <span style="transform:rotate(45deg);font-size:15px"></span>
      </div>`,
      iconSize: [36, 36], iconAnchor: [0, 0], popupAnchor: [10, -5],
    });

    const marker = L.marker([coordinates[1], coordinates[0]], { icon: destIcon })
      .addTo(map)
      .bindPopup(`<div><b>${city}</b><br>${route.distance}</div>`)
      .openPopup();

    routeLayerRef.current = L.layerGroup([outline, polyline]);
    destMarkerRef.current = marker;

    map.fitBounds(polyline.getBounds(), { padding: [50, 50] });

    if (infoRef.current) {
      infoRef.current.innerHTML = `
        <div style="padding:14px;border-top-left-radius:16px;border-top-right-radius:16px;background-color:white;">
          <h2 style="font-size:1.2rem; font-weight:700; color:#111827;margin-bottom:6px;">Route de la commande</h2>
          <div  style="display:flex; align-items:center; gap:3px;" >
            <span style="display:flex; align-items:center; gap:2px;">
              <img src="${`/images/${appConfig.slug}-fav.png`}" style="width:18px;height:18px;" />
              Casablanca
            </span>
            <span style="color:#6b7280;margin:0 8px">→</span>
            <span>📍 ${city}</span>
          </div>
          <div style="margin-top:5px;margin-bottom:2px;">
            <span style="background:#eff6ff;color:#1d4ed8;padding:3px 6px;border-radius:20px;font-size:12px">${route.distance}</span>
            <span style="margin-left:8px;background:#f0fdf4;color:#166534;padding:3px 6px;border-radius:20px;font-size:12px">⏱ ${route.duration}</span>
          </div>
        </div>
      `;
    }
  };

  // Init map once — then draw route immediately if city is already set
  useEffect(() => {
    const init = () => {
      if (mapInstance.current || !mapRef.current) return;
      const L = window.L;

      const map = L.map(mapRef.current, {
        center: [31.7917, -7.0926],
        zoom: 6,
        zoomControl: true,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        attribution: "© OpenStreetMap © CARTO",
        subdomains: "abcd",
        maxZoom: 19,
      }).addTo(map);

      const originIcon = L.divIcon({
        className: "",
        html: `<div style="width:25px;height:25px;background:#fff;border-radius:50%;border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-size:17px;box-shadow:0 2px 12px rgba(29,78,216,0.5)">
        <img src="${`/images/${appConfig.slug}-fav.png`}" style="width:18px;height:18px;"  />
        </div>`,
        iconSize: [38, 38], iconAnchor: [10, 10], popupAnchor: [0, -22],
      });

      L.marker([ORIGIN.lat, ORIGIN.lng], { icon: originIcon })
        .addTo(map)
        .bindPopup(`<b>Casablanca</b><br>${appConfig.name}`);

      mapInstance.current = map;

      // 👇 Draw route right after map is ready, using the latest city value
      if (cityRef.current) {
        drawRoute(cityRef.current);
      }
    };

    if (window.L) {
      init();
    } else {
      const s = document.createElement("script");
      s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      s.onload = init;
      document.head.appendChild(s);
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  // Redraw route when city changes (map already initialized)
  useEffect(() => {
    if (mapInstance.current) {
      drawRoute(city);
    }
    // If map isn't ready yet, the init callback above will handle it
  }, [city]);

  return (
    <div>
      <div
        ref={infoRef}
        style={{
          display: city ? "flex" : "none",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          fontSize: 13,
          fontWeight: 500,
          flexWrap: "wrap",
          gap: 4,
        }}
      />
      <div ref={mapRef}
        className="rounded-b-2xl shadow-lg z-50"
        style={{ width: "100%", height: 400 }} />
    </div>
  );
}